
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '../../filterables/TableFilterable.vue';
import FilterWidget from '../shared/FilterWidget.vue';
import {
	PermissionsGroup,
 } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { addComma } from '../../../../../util/number';
import moment, { Moment } from 'moment';
import { getTableName } from '@/worker/db/sqlHelpers';
@Component({
	components: {
		TableFilterable,
	},
})
export default class ForecastActualResultComparisonTable extends FilterWidget {
	@Prop({ default: () => [] }) public forecastSales!: number[];
	@Prop({ default: () => [] }) public forecastGrossProfits!: number[];
	@Prop({ default: () => [] }) public forecastExpenses!: number[];
	@Prop({ default: () => [] }) public forecastNetProfits!: number[];

	public sales: number[] = [];
	public grossProfits: number[] = [];
	public expenses: number[] = [];

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange'];

	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}
	public get dateFormatted() {
		return this.selectedDateRange;
	}
	public get monthValues() {
		const [d0, d1] = this.selectedDateRange.map((d) => moment(d));
		const period = d1.diff(d0, 'month') + 1;
		const result: Moment[] = [];
		for (let i = 0; i < period; i++) {
			result.push(d0.clone());
			d0.add(1, 'month');
		}
		return result;
	}
	public get months() {
		return this.monthValues.map((m) => m.format('MMM YYYY'));
	}
	public get expensiveHook() {
		const { selectedDateRange } = this;
		return JSON.stringify([selectedDateRange]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['transactions', 'stockBalances'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.6);

		const txnTable = getTableName('transactions');
		const sbTable = getTableName('stockBalances');
		const months = this.monthValues;
		const monthCount = months.length;

		const monthCases = months.map((m, i) => {
			const s = m.clone().startOf('month').valueOf();
			const e = m.clone().endOf('month').valueOf();
			return `WHEN TRY_CAST("DOCDATE" AS BIGINT)`
				+ ` BETWEEN ${s} AND ${e} THEN ${i}`;
		}).join(' ');

		const [txnRows, sbRows] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "ACCTYPE" as accType,
					CASE ${monthCases} END as mIdx,
					SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
				FROM ${txnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT)
					BETWEEN ${this.selectedDateRange[0]}
					AND ${this.selectedDateRange[1]}
					AND "ACCTYPE" IN ('SA', 'SL', 'CO', 'EP')
				GROUP BY "ACCTYPE", mIdx
			`),
			queryHead.rawQuery(`
				SELECT "SYEAR" as yr, "SMONTH" as mo,
					SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
				FROM ${sbTable}
				GROUP BY "SYEAR", "SMONTH"
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.6, 0.9);

		const accSums: { [accType: string]: number[] } = {
			SA: new Array(monthCount).fill(0),
			SL: new Array(monthCount).fill(0),
			CO: new Array(monthCount).fill(0),
			EP: new Array(monthCount).fill(0),
		};
		for (const r of txnRows) {
			const idx = Number(r.mIdx);
			const at = String(r.accType);
			if (idx >= 0 && idx < monthCount && accSums[at]) {
				accSums[at][idx] = Number(r.totalAmt) || 0;
			}
		}

		const sbMap: { [key: string]: number } = {};
		for (const r of sbRows) {
			const key = Number(r.yr) + '-' + Number(r.mo);
			sbMap[key] = (sbMap[key] || 0) + (Number(r.totalAmt) || 0);
		}

		const salesArr: number[] = [];
		const grossProfitsArr: number[] = [];
		const expensesArr: number[] = [];

		for (let i = 0; i < monthCount; i++) {
			const m = months[i];
			const salesAmt = -(accSums.SA[i] + accSums.SL[i]);
			salesArr.push(salesAmt);

			const costAmt = -accSums.CO[i];
			const prev = m.clone().subtract(1, 'month');
			const prevKey = prev.year() + '-' + (prev.month() + 1);
			const currKey = m.year() + '-' + (m.month() + 1);
			const stockMov = (sbMap[prevKey] || 0) - (sbMap[currKey] || 0);
			grossProfitsArr.push(salesAmt + costAmt + stockMov);

			expensesArr.push(accSums.EP[i]);
		}

		this.sales = salesArr;
		this.grossProfits = grossProfitsArr;
		this.expenses = expensesArr;

		this.saveHistory('sales', 'grossProfits', 'expenses');
		this.generateLoadingText(1);
	}

	public get netProfits() {
		return this.grossProfits.map(
			(grossProfits, i) => grossProfits - this.expenses[i],
		);
	}

	public get tableFields() {
		return [
			{
				key: 'key',
				stickyColumn: true,
				label: '',
				variant: 'light font-weight-bold',
			},
			...this.months.map((month) => ({
				key: month,
			})),
			{ key: 'total', variant: 'forecast-total' },
			{ key: 'average' },
		];
	}

	public get tableItems() {
		const monthTotalAvg = [...this.months, 'total', 'average'];
		function addTotalAvg(array: number[] | string[]) {
			let total = 0;
			const len = array.length;
			const added: number[] = [];
			for (const num of array) {
				const n = Number(num) || 0;
				total += n;
				added.push(n);
			}
			added.push(total, total / len);
			return added;
		}
		// const processed = {
		// 	sales: addTotalAvg(this.sales),
		// 	grossProfits: addTotalAvg(this.grossProfits),
		// 	expenses: addTotalAvg(this.expenses),
		// 	netProfits: addTotalAvg(this.netProfits),
		// 	forecastSales: addTotalAvg(this.forecastSales),
		// 	forecastGrossProfits: addTotalAvg(this.forecastGrossProfits),
		// 	forecastExpenses: addTotalAvg(this.forecastExpenses),
		// 	forecastNetProfits: addTotalAvg(this.forecastNetProfits),
		// };
		const processed = {
			sales: addTotalAvg(this.sales),
			grossProfits: addTotalAvg(this.grossProfits),
			expenses: addTotalAvg(this.expenses),
			netProfits: addTotalAvg(this.netProfits),
			forecastSales: addTotalAvg(this.forecastSales),
			forecastGrossProfits: addTotalAvg(this.forecastGrossProfits),
			forecastExpenses: addTotalAvg(this.forecastExpenses),
			forecastNetProfits: addTotalAvg(this.forecastNetProfits),
		};

		// const format = TableItemFormatter.currency;
		const format = (n: number) => addComma(n, gdbx.numDecimal, true);

		const toItem = (o, n, i) => {
			o[monthTotalAvg[i]] = format(n);
			return o;
		};
		const salesRow = processed.sales.reduce(
			(o, num, i) => {
				const n = Number(num) || 0;
				const predicted = Number(processed.forecastSales[i]) || 0;
				const margin = n - predicted;
				const rate = n !== 0 ? (margin / n) * 100 : predicted;
				const positive = margin >= 0;
				o[monthTotalAvg[i]] = `${format(n)}(${positive ? '+' : ''}${rate.toFixed(
					1,
				)}%)`;
				o._cellVariants[monthTotalAvg[i]] = positive
					? 'forecast-cell-success'
					: 'forecast-cell-danger';
				return o;
			},
			{
				key: 'Actual Sales',
				_cellVariants: {},
			},
		);
		const forecastSales = processed.forecastSales.reduce(toItem, {
			key: 'Predicted Sales',
		});
		// const avgGrossProfits = processed.grossProfits.reduce(
		// 	(o, num, i) => {
		// 		const n = Number(num);
		// 		o.total += n;
		// 		o.avg[i] = o.total / (i + 1);
		// 		return o;
		// 	},
		// 	{
		// 		avg: [] as number[],
		// 		total: 0,
		// 	},
		// );
		const grossProfitsRow = processed.grossProfits.reduce(
			(o, num, i) => {
				const n = Number(num) || 0;
				const sale = Number(processed.sales[i]) || 0;
				const rate = sale !== 0 ? (n / sale) * 100 : 0;
				o[monthTotalAvg[i]] = `${format(n)}(${rate.toFixed(1)}%)`;
				const predicted = Number(processed.forecastGrossProfits[i]) || 0;
				const margin = n - predicted;
				const positive = margin >= 0;
				o._cellVariants[monthTotalAvg[i]] = positive
					? 'forecast-cell-success'
					: 'forecast-cell-danger';
				return o;
			},
			{
				key: 'Actual Gross Profit',
				_cellVariants: {},
			},
		);
		const forecastGrossProfits = processed.forecastGrossProfits.reduce(
			(o, num, i) => {
				const n = Number(num) || 0;
				const sale = Number(processed.forecastSales[i]) || 0;
				const rate = sale !== 0 ? (n / sale) * 100 : 0;
				o[monthTotalAvg[i]] = `${format(n)}(${rate.toFixed(1)}%)`;
				return o;
			},
			{
				key: 'Predicted Gross Profit',
				_cellVariants: {},
			},
		);
		const expensesRow = processed.expenses.reduce(
			(o, num, i) => {
				const n = Number(num) || 0;
				const predicted = Number(processed.forecastExpenses[i]) || 0;
				const margin = n - predicted;
				const rate = n !== 0 ? (margin / n) * 100 : predicted;
				const positive = margin >= 0;
				const good = margin <= 0;
				o[monthTotalAvg[i]] = `${format(n)}(${positive ? '+' : ''}${rate.toFixed(
					1,
				)}%)`;
				o._cellVariants[monthTotalAvg[i]] = good
					? 'forecast-cell-success'
					: 'forecast-cell-danger';
				return o;
			},
			{
				key: 'Actual Expenses',
				_cellVariants: {},
			},
		);
		const forecastExpenses = processed.forecastExpenses.reduce(toItem, {
			key: 'Predicted Expenses',
		});
		const netProfitsRow = processed.netProfits.reduce(
			(o, num, i) => {
				const n = Number(num) || 0;
				const predicted = Number(processed.forecastNetProfits[i]) || 0;
				const margin = n - predicted;
				const rate = n !== 0 ? (margin / n) * 100 : predicted;
				const positive = margin >= 0;
				o[monthTotalAvg[i]] = `${format(n)}(${positive ? '+' : ''}${rate.toFixed(
					1,
				)}%)`;
				o._cellVariants[monthTotalAvg[i]] = positive
					? 'forecast-cell-success'
					: 'forecast-cell-danger';
				return o;
			},
			{
				key: 'Actual Net Profit',
				_cellVariants: {},
			},
		);
		const forecastNetProfits = processed.forecastNetProfits.reduce(toItem, {
			key: 'Predicted Net Profit',
		});
		function addTotalVariance(o: any) {
			o._cellVariants.total += ' table-forecast-total';
		}
		addTotalVariance(salesRow);
		addTotalVariance(grossProfitsRow);
		addTotalVariance(expensesRow);
		addTotalVariance(netProfitsRow);

		const result: any[] = [
			forecastSales,
			salesRow,
			forecastGrossProfits,
			grossProfitsRow,
			forecastExpenses,
			expensesRow,
			forecastNetProfits,
			netProfitsRow,
		];
		return result;
	}

	public rowClass(
		item: any,
		type: 'row' | 'row-details' | 'row-top' | 'row-bottom' | 'table-busy',
	) {
		// if (!item || type !== 'row') return;
		// if (item.key === 'Actual Sales') return 'text-success';
	}
}
